import type { RenderContext, VideoFrame } from '../../types';
import { WebGLShaderProgram } from '../../gpu/shader';
import { BaseEffect } from '../base';
import { VERTEX_FULLSCREEN, FRAG_FILM_GRAIN } from '../../shaders/glsl';
import { withOverlay } from '../environmental/composite';
import { hash01 } from '../../utils/deterministic';

const DEFAULTS = { amount: 0.45, size: 0.35 };

let grainProg: WebGLShaderProgram | null = null;

export class FilmGrainEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../../types').EffectTimelineClip>) {
    super(id, 'film_grain', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const gl = context.gpu!.gl;
    if (!grainProg) grainProg = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_FILM_GRAIN);
    return this.runShaderPass(frame, grainProg, (_gl, prog) => {
      prog.setUniform1i('u_texture', 0);
      prog.setUniform1f('u_amount', params.amount);
      prog.setUniform1f('u_size', params.size);
      prog.setUniform1f('u_time', context.frameIndex / 24);
    }, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    return withOverlay(frame, (ctx, w, h) => {
      const step = Math.max(2, Math.round(3 + params.size * 4));
      const amount = params.amount * 55;
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const n = hash01(x, y + context.frameIndex * 17, 99) - 0.5;
          const a = Math.abs(n) * amount * 0.01;
          if (a < 0.01) continue;
          ctx.fillStyle = n > 0 ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${a})`;
          ctx.fillRect(x, y, step, step);
        }
      }
    }, 'source-over');
  }
}
